import Vue from 'vue';
import { sleeper } from '@/lib/tools';
import router from '@/router';

export const state = {
  groupBy: null, // account, acc_group, cross_tag
  id: null,
  ids: null,
  detail: {},
  loading: {
    fetchList: false,
    fetchDetail: false,
  },
  selectedAccount: {},
  logtime: 0,
};

export const actions = {
  async fetchList({ state, commit, dispatch, rootState, rootGetters }, { force = false }) {
    commit('updateId', null);
    if (rootState.vendor === 'azure') {
      commit('updateIds', null);
      commit('updateSelectedAccount', {});
    }
    switch (state.groupBy) {
      case 'account':
        await dispatch('accounts/fetchAccounts', { force: force }, { root: true });

        // Set ID if ID in state is null.
        if (!state.id) {
          const accounts = rootGetters['accounts/getAccounts'];
          if (accounts.length) {
            const account = accounts[0];
            if (account) {
              switch (rootState.vendor) {
                case 'azure':
                  commit('updateId', account.account_id);
                  commit('updateIds', account.account_ids);
                  commit('updateSelectedAccount', account);
                  break;
                default:
                  commit('updateId', account.account_id);
                  break;
              }
              commit('accounts/updateSelectedId', account.account_id, { root: true });
            }
          }
        }
        break;
      case 'azure_subscription':
        await dispatch('accounts/fetchAzureSubscriptions', { force: force }, { root: true });
        if (!state.id) {
          const accounts = rootGetters['accounts/getAccounts'];
          if (accounts.length) {
            const account = accounts[0];
            if (account) {
              commit('updateId', account.account_id);
              commit('updateIds', account.account_ids);
              commit('updateSelectedAccount', account);
              commit('accounts/updateSelectedId', account.account_id, { root: true });
            }
          }
        }
        break;
      case 'acc_group':
        await dispatch('accounts/fetchAccounts', { force: force }, { root: true });
        await dispatch('groups/fetchGroups', { force: force }, { root: true });

        // Set ID if ID in state is null.
        if (!state.id) {
          const groups = rootGetters['groups/getGroups'];
          if (groups.length) {
            const id = groups[0].group_id;
            commit('updateId', id);
            commit('groups/updateSelectedId', id, { root: true });
          }
        }
        break;
      case 'cross_tag':
        await dispatch('crossTags/fetchCrossTags', { force: force }, { root: true });

        // Set ID if ID in state is null.
        if (!state.id) {
          const crossTags = rootGetters['crossTags/getCrossTags'];
          if (crossTags.length) {
            const id = crossTags[0].id;
            commit('updateId', id);
            commit('crossTags/updateSelectedId', id, { root: true });
          }
        }
        break;
      case 'report_filter':
        await dispatch(
          'reportFilters/fetchReportFilters',
          { vendor: rootState.vendor },
          { root: true }
        );

        // Set ID if ID in state is null.
        if (!state.id) {
          const reportFilters = rootGetters['reportFilters/getFiltersOptions'](rootState.vendor);
          if (reportFilters.length) {
            const id = reportFilters[0].value;
            commit('updateId', id);
            commit('reportFilters/setActiveFilter', { filterId: id }, { root: true });
          }
        }
        break;
    }
  },

  /**
   * Fetch Detail
   * @param state - groupBy
   * @param commit
   * @param dispatch
   * @param force {boolean} - default: false
   * @return {Promise<void>}
   */
  // async fetchDetail({ state, commit, dispatch, rootState }, { force = false }) {
  async fetchDetail(
    { state, commit, dispatch, rootState },
    { force = false, fetchCostForecast = false }
  ) {
    try {
      commit('updateLoading', { key: 'fetchDetail', val: true });

      if (!force) await sleeper(500);

      switch (state.groupBy) {
        case 'account':
          // Request Detail
          await dispatch('accounts/fetchDetail', { force: force }, { root: true });

          // Request Cost Forecast data
          if (rootState.vendor === 'aws' && fetchCostForecast) {
            await dispatch('budget/fetchBudget', { force: force }, { root: true });
            dispatch('costForecast/fetchForecastMonthly', { force: force }, { root: true });
            dispatch('costForecast/fetchForecastMonthToDate', { force: force }, { root: true });
            dispatch('costForecast/fetchForecastMonthOnMonth', { force: force }, { root: true });
          }

          // Request Analyze data
          await dispatch('fetchAnalyzeData');
          break;

        case 'azure_subscription':
          // Request Detail
          await dispatch('accounts/fetchDetail', { force: force }, { root: true });

          // Request Analyze data
          await dispatch('fetchAnalyzeData');
          break;

        case 'acc_group':
          // Request Detail
          await dispatch('groups/fetchGroupDetails', null, { root: true });

          if (rootState.vendor === 'aws' && fetchCostForecast) {
            // Request Cost Forecast data
            await dispatch('budget/fetchBudget', { force: force }, { root: true });
            dispatch('costForecast/fetchForecastMonthly', { force: force }, { root: true });
            dispatch('costForecast/fetchForecastMonthToDate', { force: force }, { root: true });
            dispatch('costForecast/fetchForecastMonthOnMonth', { force: force }, { root: true });
          }

          // Request Analyze data
          await dispatch('fetchAnalyzeData');

          break;
        case 'cross_tag':
          // Request Detail
          await dispatch('crossTags/fetchCrossTagDetail', null, { root: true });

          // Request Analyze data
          await dispatch('fetchAnalyzeData');
          break;
        case 'report_filter':
          // Request Analyze data
          await dispatch('fetchAnalyzeData');
          break;
      }

      commit('updateLoading', { key: 'fetchDetail', val: false });
      return Promise.resolve();
    } catch (e) {
      console.log(e);
      commit('updateLoading', { key: 'fetchDetail', val: false });
      commit('reset', state);
      await dispatch('fetchList', { force: true });
      router.push('/usage_reports').catch(() => {});
      window.location.reload();
      return Promise.resolve();
    }
  },

  async fetchAnalyzeData({ dispatch }) {
    //  Request Analyze Chart data
    await dispatch('setAnalyzeChartConditions');
    await dispatch('reports_v2/fetchGraphData', null, { root: true });
  },

  setAnalyzeChartConditions({ commit, getters, rootState }) {
    /**
     * Set chart owner
     */
    commit('reports_v2/updateCondition', { key: 'owner', value: getters.getOwner }, { root: true });
    if (getters.getOwnerId) {
      commit(
        'reports_v2/updateCondition',
        { key: 'ownerId', value: getters.getOwnerId },
        { root: true }
      );
    }

    /**
     * Set chart config from User data.
     */
    commit(
      'reports_v2/updateCondition',
      { key: 'vendor', value: rootState.vendor },
      { root: true }
    );

    return Promise.resolve();
  },
};
export const mutations = {
  updateLoading(state, { key, val }) {
    state.loading[key] = val;
  },
  updateLogtime(state, val) {
    state.logtime = val;
  },
  /**
   * Update ID
   * @param state {object}
   * @param id {string} - account id, group id, cross tag id
   */
  updateId(state, id) {
    state.id = id;
  },

  /**
   * Update IDs
   * @param state {object}
   * @param id {string} - account ids
   */
  updateIds(state, ids) {
    state.ids = ids;
  },

  updateSelectedAccount(state, account) {
    state.selectedAccount = account;
  },

  /**
   * Update Group by
   * @param state {object}
   * @param val {string} - account, acc_group, cross_tag, azure_subscription
   */
  updateGroupBy(state, val) {
    state.groupBy = val;
  },

  /**
   * Update Detail
   * @param state {object}
   * @param data {object}
   * @param hash {string} - account id, group id, cross tag id
   */
  updateDetail(state, { data, reqHash }) {
    Vue.set(state.detail, reqHash, data);
  },

  reset(state) {
    state.id = null;
    state.groupBy = null;
  },
};
export const getters = {
  /**
   * Get level for API request
   * @return {string} - account, acctgroup
   */
  getLevel(state) {
    switch (state.groupBy) {
      case 'account':
        return 'account';

      case 'acc_group':
        return 'acctgroup';
      default:
        return '';
    }
  },

  /**
   * Detail of account/acc group/cross tag
   * @param state
   * @return {object|null}
   */
  getDetail(state) {
    return state.detail[state.id] ? state.detail[state.id] : null;
  },

  getFormattedDetail(state, getters) {
    switch (state.groupBy) {
      case 'account':
        return getters.getFormattedAccountDetail;

      case 'azure_subscription':
        return getters.getFormattedAccountDetail;

      case 'acc_group':
        return getters.getFormattedGroupDetail;

      case 'cross_tag':
        return getters.getFormattedCrossTagDetail;
    }
  },

  /**
   * Formatted detail of account
   * @param state
   * @param getters
   * @param rootState
   * @return {object}
   */
  getFormattedAccountDetail(state, getters, rootState) {
    const detail = getters.getDetail;
    if (!detail) return {};

    // Alert
    const isAlertEnabled = () => {
      const alert = detail[`${rootState.userState.dataType}_notification`];
      if (!alert) return false;

      return Object.keys(alert).some(i => {
        if (i === 'daily' || i === 'monthly' || i === 'previous_day') {
          return alert[i];
        }
      });
    };

    return {
      basic: {
        name: detail.account_name,
        id: detail.account_id ? detail.account_id : null,
        payer: detail.payer ? 'yes' : 'no',
        payer_id: detail.payer_id,
      },
      alert: {
        state: isAlertEnabled() ? 'set' : 'not set yet',
      },
    };
  },

  /**
   * Formatted detail of group
   * @param state
   * @param getters
   * @param rootState
   * @param rootGetters
   * @return {object}
   */
  getFormattedGroupDetail(state, getters, rootState, rootGetters) {
    const detail = getters.getDetail;
    if (!detail) return {};

    const accounts = [];

    if (detail.accounts) {
      detail.accounts.forEach(i => {
        const target = rootGetters['accounts/getAccounts'].find(acc => acc.account_id === i);

        if (target) {
          accounts.push({
            id: target.account_id,
            name: target.account_name,
          });
        }
      });
    }

    return {
      basic: {
        name: detail.group_name ? detail.group_name : null,
        id: detail.group_id ? detail.group_id : null,
      },
      alert: {},
      accounts: accounts,
    };
  },

  /**
   * Formatted detail array of Cross tag
   * @param state
   * @param getters
   * @return {object}
   */
  getFormattedCrossTagDetail(state, getters) {
    const detail = getters.getDetail;
    if (!detail) return {};

    const tagKey = detail.mainTagKey ? detail.mainTagKey : '-';
    const tagValue = detail.mainTagValue ? detail.mainTagValue : '-';

    const name = `${tagKey} | ${tagValue}`;

    return {
      basic: {
        name: name,
        id: detail.id ? detail.id : null,
      },
      alert: {},
      tags: detail.tags,
    };
  },

  /**
   * ID of account/acc group/cross tag
   * @param state
   * @return {string}
   */
  getId(state) {
    return state.id ? state.id : '';
  },

  getSelectedAccount(state) {
    return state.selectedAccount;
  },

  /**
   * ID of account/acc group/cross tag
   * account: account_name
   * acc group: group_name
   * @param state
   * @param getters
   * @return {string}
   */
  getName(state, getters) {
    if (!getters.getDetail) return '-';

    switch (state.groupBy) {
      case 'account':
        return getters.getFormattedAccountDetail.basic.name;
      case 'azure_subscription':
        return getters.getFormattedAccountDetail.basic.name;
      case 'acc_group':
        return getters.getFormattedGroupDetail.basic.name;
      case 'cross_tag':
        if (state.id === 'nontag') {
          return 'Nontag';
        }
        return getters.getFormattedCrossTagDetail.basic.name;
      default:
        return '-';
    }
  },

  /**
   * Return Owner
   * @param state
   * @return {string}
   */
  getOwner(state) {
    switch (state.groupBy) {
      case 'account':
        return 'account';
      case 'acc_group':
        return 'group';
      case 'cross_tag':
        return 'tagresource';
      case 'report_filter':
        return 'report_filter';
      case 'azure_subscription':
        return 'azure_subscription';
      default:
        return '';
    }
  },

  /**
   * Return Owner ID
   * @param state
   * @param getters
   * @return {string|null}
   */
  getOwnerId(state, getters, rootState) {
    const detail = getters.getDetail;
    if (state.groupBy !== 'report_filter' && !detail) return null;

    switch (state.groupBy) {
      case 'account':
        if (rootState.vendor === 'azure') {
          return `${state.ids}|${detail.payer_id}`;
        }
        return `${state.id}|${detail.payer_id}`;
      case 'azure_subscription':
        return `${state.ids}|${detail.payer_id}`;
      case 'acc_group':
        return state.id;
      case 'cross_tag':
        return state.id;
      case 'report_filter':
        return state.id;
      default:
        return null;
    }
  },

  /**
   * log interval
   * @param state
   * @return {integer}
   */
  getLogtimeInterval(state) {
    return state.logtime ? Date.now() - state.logtime : 0;
  },
};
